import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, computed } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { CommonModule } from '@angular/common';
import { InputComponent } from "../input/input.component";
import { SelectedComponent } from "../selected/selected.component";
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { PENSIONER_INPUTS, PENSIONER_SELECTORS } from 'app/config/constants.config';
import {
  buildPreloadedConfig,
  buildPreloadedSelectConfig,
  normalizeFieldValue,
  mergeSelectOptionsWithPreload,
  resolveOptions,
  resolveRawFieldValue,
} from '../utils/field-preload.utils';

@Component({
  selector: 'app-pensioners',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    SelectedComponent,
    AutocompleteComponent
  ],
  templateUrl: './pensioners.component.html'
})
export class PensionersComponent implements OnInit, OnChanges, OnDestroy {
  form: FormGroup;
  dataInputs = PENSIONER_INPUTS;
  dataSelected = PENSIONER_SELECTORS;

  @Output() formValid = new EventEmitter<any>();
  @Input() defaultData: any;
  @Output() validData = new EventEmitter<any>();
  @Input() isCheckData = false;
  @Input() dataLastOnboarding: any;
  @Input() isLightVersion = false;
  @Input() resetTimer = false;

  pensionFunds: any[] = [];
  codpros: any[] = [];
  private isUpdating = false;
  private subscription: any;

  lightDataInputs = computed(() => {
    if (!this.isLightVersion) return this.dataInputs();
    return this.dataInputs().filter((input: any) =>
      input.formName === 'income'
    );
  });

  lightDataSelected = computed(() => {
    if (!this.isLightVersion) return this.dataSelected();
    return [];
  });

  constructor(
    private _listService: ListsService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = new FormGroup({
      fonpen: new FormControl(null, [Validators.required]),
      resolution: new FormControl(null, []),
      academic: new FormControl(null, [Validators.required]),
      specialProtecionProgram: new FormControl(null, [Validators.required]),
      since: new FormControl(null, [Validators.required]),
      codpro: new FormControl(null, [Validators.required]),
      income: new FormControl(null, [Validators.required]),
      incomeExtraActivity: new FormControl(null, [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.getAllAcademicLevels();
    this.getAllSpecialProgram();
    this.getAllProfessions();
    this._listService.getPensioner().subscribe(res => { this.pensionFunds = res; });
    this.updateFormData();

    this.subscription = this.form.valueChanges.subscribe((value) => {
      if (!this.isUpdating) {
        setTimeout(() => {
          this.emitFormData(value);
        }, 50);
      }
    });
  }

  private preloadSnapshot = '';

  private getPreloadKey(data: any): string {
    return JSON.stringify({
      fonpen: data.fonpen,
      resolution: data.resolution,
      academic: resolveRawFieldValue(data, 'academic'),
      specialProtecionProgram: resolveRawFieldValue(data, 'specialProtecionProgram'),
      since: data.since,
      codpro: data.codpro,
      income: data.income,
      incomeExtraActivity: data.incomeExtraActivity,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['defaultData']?.currentValue) return;

    const newData = changes['defaultData'].currentValue;
    if (!newData || !this.hasAddressData(newData)) return;

    const key = this.getPreloadKey(newData);
    if (key === this.preloadSnapshot) return;

    const isFirstPopulation = this.preloadSnapshot === '';
    this.preloadSnapshot = key;

    if (isFirstPopulation) {
      this.updateFormData();
    }
  }

  private hasAddressData(data: any): boolean {
    return data?.asotip != null && data?.asotip !== '';
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private emitFormData(value: any) {
    this.formValid.emit({
      ...value,
      _isValid: this.form.valid,
      _formName: 'pensioners'
    });
  }

  private updateFormData(): void {
    this.isUpdating = true;
    const newData = this.defaultData;

    if (newData && Object.keys(newData).length > 0) {
      this.form.patchValue({
        fonpen: newData.fonpen ?? null,
        resolution: normalizeFieldValue(newData.resolution, 'date') || null,
        academic: resolveRawFieldValue(newData, 'academic') ?? null,
        specialProtecionProgram: resolveRawFieldValue(newData, 'specialProtecionProgram') ?? null,
        since: normalizeFieldValue(newData.since, 'date') || null,
        codpro: newData.codpro ?? null,
        income: normalizeFieldValue(newData.income, 'number') || null,
        incomeExtraActivity: normalizeFieldValue(newData.incomeExtraActivity, 'number') || null,
      }, { emitEvent: false });

      this.dataInputs.update((inputs: any) =>
        inputs.map((input: any) => buildPreloadedConfig(input, newData[input.formName], input.type))
      );

      this.dataSelected.update((selects: any) =>
        selects.map((select: any) =>
          buildPreloadedSelectConfig(select, newData[select.formName], select.options, newData)
        )
      );
    }

    setTimeout(() => {
      this.isUpdating = false;
    }, 200);
  }

  private refreshSelectConfigs(): void {
    const newData = this.defaultData;
    if (!newData || Object.keys(newData).length === 0) return;

    this.dataSelected.update((selects: any) =>
      selects.map((select: any) => {
        const currentOptions = resolveOptions(select.options);
        const mergedOptions = mergeSelectOptionsWithPreload(currentOptions, newData, select.formName);
        if (mergedOptions.length !== currentOptions.length && select.options?.set) {
          select.options.set(mergedOptions);
        }
        return buildPreloadedSelectConfig(select, newData[select.formName], select.options, newData);
      })
    );
  }

  getAllProfessions() {
    this._listService.getCodpro().subscribe({
      next: (response: any) => { this.codpros = response; },
      error: (error: any) => { console.error('Error cargando profesiones:', error); }
    });
  }

  getAllSpecialProgram() {
    this._listService.getAllSpecialProgram().subscribe({
      next: (response: any) => {
        const merged = mergeSelectOptionsWithPreload(
          response,
          this.defaultData,
          'specialProtecionProgram'
        );
        this.dataSelected().at(1)?.options.set(merged);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading special programs:', error); }
    });
  }

  getAllAcademicLevels() {
    this._listService.getAllAcademicLevels().subscribe({
      next: (response: any) => {
        this.dataSelected().at(0)?.options.set(response);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading academic levels:', error); }
    });
  }

  setFonpen(fonpenId: number | null) {
    this.isUpdating = true;
    this.form.get('fonpen')!.setValue(fonpenId);
    this.emitFormData(this.form.value);
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  setFieldEnabled(event: { formName: string; enabled: boolean }): void {
    this.dataInputs.update((inputs: any) =>
      inputs.map((input: any) =>
        input.formName === event.formName ? { ...input, enabled: event.enabled } : input
      )
    );

    this.dataSelected.update((selects: any) =>
      selects.map((select: any) =>
        select.formName === event.formName ? { ...select, enabled: event.enabled } : select
      )
    );
  }

  setCodpro(event: { value: string | number | null } | string | number | null) {
    const codproId = event && typeof event === 'object' && 'value' in event
      ? event.value
      : event;

    this.isUpdating = true;
    this.form.patchValue({ codpro: codproId }, { emitEvent: false });
    this.emitFormData(this.form.value);
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  handleValue(event: { formName: string, value: string | number, isValid?: boolean, isDirty?: boolean }) {
    const control = this.form.get(event.formName);
    if (control?.value == event.value) return;

    this.isUpdating = true;
    control?.setValue(event.value, { emitEvent: false });
    this.emitFormData(this.form.value);
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  getPensionFundLabel(value: any): string {
    if (!value && value !== 0) return 'No especificado';
    const fund = this.pensionFunds.find(f => f.value == value);
    return fund ? fund.label : value.toString();
  }

  getSelectOptionLabel(formName: string, value: any): string {
    if (!value && value !== 0) return 'No especificado';
    const selectConfig = this.dataSelected().find(config => config.formName === formName);
    if (selectConfig && selectConfig.options) {
      const option = selectConfig.options().find((opt: any) => opt.value == value);
      return option ? option.label : value.toString();
    }
    return value.toString();
  }

  getProfessionLabel(value: any): string {
    if (!value && value !== 0) return 'No especificado';
    const profession = this.codpros.find(p => p.value == value);
    return profession ? profession.label : value.toString();
  }
}
